import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';

/**
 * `<nb-chip>` — a filter/toggle chip button (Mockup `.chip`).
 *
 * Renders a real `<button>` so keyboard + focus semantics come for free.
 * Project an optional `<nb-icon>` (sized to 15px via `--nb-icon-size`) and the
 * label text. The `selected` model input toggles the `.on` look (brand fill);
 * clicking flips it and emits `(selectedChange)`. `aria-pressed` reflects the
 * toggle state so the chip is never colour-only for assistive tech.
 *
 * Group chips inside `<nb-chip-row>` for the horizontal scrolling filter bar.
 * `count` renders a small trailing bubble (2026-09 admin prototype `.chip .n`)
 * — part of the accessible name, readable in both states and both themes.
 *
 * @example
 * <nb-chip [(selected)]="showOpen">Åpne</nb-chip>
 * <nb-chip [(selected)]="nearby"><nb-icon [icon]="MapPin"/> I nærheten</nb-chip>
 */
@Component({
  selector: 'nb-chip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.on]': 'selected()',
  },
  template: `<button
    class="chip"
    type="button"
    [class.on]="selected()"
    [attr.aria-pressed]="selected()"
    (click)="toggle()"
  >
    <ng-content />
    @if (count() !== null) {
      <span class="n">{{ count() }}</span>
    }
  </button>`,
  styleUrl: './chip.css',
})
export class NbChip {
  /** Two-way selected state — toggles the `.on` (brand) appearance. */
  readonly selected = model(false);
  /** Optional trailing count bubble. `null` hides it. */
  readonly count = input<number | null>(null);

  protected toggle(): void {
    this.selected.set(!this.selected());
  }
}
